import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess({ onNavigate }) {
  const [status, setStatus] = useState("checking"); // checking | success | error
  const [attempts, setAttempts] = useState(0);

  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    try {
      const res = await fetch(`${API}/payments/checkout-status/${sessionId}`, {
        credentials: "include",
      });
      const data = await res.json();

      if (data.payment_status === "paid") {
        setStatus("success");
        // Clean URL
        window.history.replaceState({}, "", "/");
        return;
      }
      if (data.status === "expired") {
        setStatus("error");
        return;
      }

      // Still pending, keep polling
      if (attempts < 8) {
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } else {
        setStatus("error");
      }
    } catch {
      if (attempts < 8) {
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {status === "checking" && (
          <>
            <Loader2 className="h-16 w-16 text-emerald-400 animate-spin mx-auto" />
            <h1 className="text-2xl font-semibold text-zinc-100">Verifying Payment...</h1>
            <p className="text-zinc-400 text-sm">Please wait while we confirm your subscription.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="h-20 w-20 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-semibold text-zinc-100" data-testid="payment-success-title">
              Welcome to Premium!
            </h1>
            <p className="text-zinc-400 text-sm">
              Your $4.99/month subscription is active. Enjoy unlimited stock tracking,
              real-time compliance alerts, and compliance history.
            </p>
            <Button
              className="bg-emerald-600 text-white hover:bg-emerald-700 mt-4"
              onClick={() => onNavigate("dashboard")}
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="h-20 w-20 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto">
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-semibold text-zinc-100" data-testid="payment-error-title">
              Payment Issue
            </h1>
            <p className="text-zinc-400 text-sm">
              We couldn't verify your payment. If you were charged, your subscription
              will activate automatically. Contact support if the issue persists.
            </p>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 mt-4"
              onClick={() => onNavigate("dashboard")}
              data-testid="back-to-dashboard-btn"
            >
              Back to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
